import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { formatDateTime } from '../../lib/dateUtils'

const PAIR_COLORS = [
  '#a7c7e7', // pastel blue
  '#b5e6b5', // pastel green
  '#f5c6aa', // pastel peach
  '#d4b5e6', // pastel purple
  '#f5e6aa', // pastel yellow
  '#e6b5b5', // pastel rose
  '#b5d4e6', // pastel teal
  '#e6d4b5', // pastel sand
]

function getDciCategory(total, t) {
  if (total == null) return null
  if (total < 112) return { label: t('submissionsTable.dciCategories.belowAvg'), color: 'bg-amber-50 text-amber-700' }
  if (total < 146) return { label: t('submissionsTable.dciCategories.normal'), color: 'bg-primary-50 text-primary-700' }
  return { label: t('submissionsTable.dciCategories.aboveAvg'), color: 'bg-emerald-50 text-emerald-700' }
}

function SortIcon({ direction }) {
  if (!direction) {
    return <span className="text-gray-300 ml-1">{'\u2195'}</span>
  }
  return (
    <span className="text-primary-500 ml-1">
      {direction === 'asc' ? '\u2191' : '\u2193'}
    </span>
  )
}

export default function SubmissionsTable({
  submissions,
  sortConfig,
  onSort,
  onRowClick,
  onDelete,
  onCoupleAnalysis,
}) {
  const { t, i18n } = useTranslation('admin')
  const locale = i18n.language?.startsWith('zh') ? 'zh-CN' : 'en'
  const enSexOptions = i18n.t('profile.q3.options', { ns: 'survey', lng: 'en', returnObjects: true })
  const currentSexOptions = i18n.t('profile.q3.options', { ns: 'survey', returnObjects: true })

  const translateSex = (val) => {
    if (!val) return val
    const idx = Array.isArray(enSexOptions) ? enSexOptions.indexOf(val) : -1
    return idx >= 0 && Array.isArray(currentSexOptions) ? currentSexOptions[idx] : val
  }

  const COLUMNS = [
    { key: 'name', label: t('submissionsTable.columns.name'), sortable: true },
    { key: 'email', label: t('submissionsTable.columns.email'), sortable: true, hideOnMobile: true },
    { key: 'biological_sex', label: t('submissionsTable.columns.sex'), sortable: true, hideOnMobile: true },
    { key: 'age', label: t('submissionsTable.columns.age'), sortable: true, hideOnMobile: true },
    { key: 'couple_name', label: t('submissionsTable.columns.couple'), sortable: true, hideOnMobile: true },
    { key: 'facilitator_name', label: t('submissionsTable.columns.facilitator'), sortable: true, hideOnMobile: true },
    { key: 'completed_at', label: t('submissionsTable.columns.submitted'), sortable: true },
    { key: 'dci_total', label: t('submissionsTable.columns.dciTotal'), sortable: true, hideOnMobile: true },
    { key: 'actions', label: '', sortable: false },
  ]
  const pairColorMap = useMemo(() => {
    const map = new Map()
    const seen = new Set()
    let colorIdx = 0

    submissions.forEach(row => {
      if (!row.partner_id || seen.has(row.id)) return
      const partnerInList = submissions.some(s => s.id === row.partner_id)
      if (!partnerInList) return

      const color = PAIR_COLORS[colorIdx % PAIR_COLORS.length]
      map.set(row.id, color)
      map.set(row.partner_id, color)
      seen.add(row.id)
      seen.add(row.partner_id)
      colorIdx++
    })

    return map
  }, [submissions])

  if (submissions.length === 0) {
    return (
      <div className="bg-white rounded-xl p-12 shadow-sm text-center">
        <div className="text-gray-400 text-lg mb-2">{t('submissionsTable.noSubmissions')}</div>
        <p className="text-gray-400 text-sm">{t('submissionsTable.noSubmissionsDetail')}</p>
      </div>
    )
  }

  const getSortDirection = (key) => {
    if (sortConfig.key !== key) return null
    return sortConfig.direction
  }

  return (
    <div className="bg-white rounded-xl shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b-2 border-gray-100">
              {COLUMNS.map(col => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap ${
                    col.sortable ? 'cursor-pointer select-none hover:text-gray-700' : ''
                  } ${col.hideOnMobile ? 'hidden md:table-cell' : ''}`}
                  onClick={() => col.sortable && onSort(col.key)}
                >
                  {col.label}
                  {col.sortable && <SortIcon direction={getSortDirection(col.key)} />}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {submissions.map(row => {
              const pairColor = pairColorMap.get(row.id)
              return (
                <tr
                  key={row.id}
                  className="border-b border-gray-50 hover:bg-primary-50 cursor-pointer transition-colors"
                  onClick={() => onRowClick(row.id)}
                >
                  <td className="px-4 py-3 font-medium text-gray-800">
                    <div className="flex items-center gap-2">
                      {pairColor && (
                        <span
                          className="inline-block w-1 h-6 rounded-full flex-shrink-0"
                          style={{ backgroundColor: pairColor }}
                          title={t('submissionsTable.linkedCouple')}
                        />
                      )}
                      {row.name}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{row.email}</td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{translateSex(row.biological_sex) || '—'}</td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{row.age ?? '—'}</td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell whitespace-nowrap">
                    {row.couple_name ? (() => {
                      const isFemale = row.biological_sex === 'Female'
                      const maleName = isFemale ? row.partner_name : row.name
                      const femaleName = isFemale ? row.name : row.partner_name
                      const partnerSubmitted = !!row.partner_id
                      return (
                        <>
                          <span className={!isFemale || partnerSubmitted ? '' : 'text-gray-300'}>{maleName}</span>
                          <span className="text-gray-300"> / </span>
                          <span className={isFemale || partnerSubmitted ? '' : 'text-gray-300'}>{femaleName}</span>
                        </>
                      )
                    })() : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-600 hidden md:table-cell">{row.facilitator_name || '—'}</td>
                  <td className="px-4 py-3 text-gray-600">{formatDateTime(row.completed_at, locale)}</td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    {row.dci_total != null ? (() => {
                      const cat = getDciCategory(row.dci_total, t)
                      return (
                        <span className={`inline-block px-2.5 py-1 rounded-full text-xs font-semibold ${cat.color}`}
                              title={`${cat.label} (${row.dci_total}/175)`}>
                          {row.dci_total}
                        </span>
                      )
                    })() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      {row.partner_id && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation()
                            onCoupleAnalysis(row.id)
                          }}
                          className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border border-primary-200 bg-primary-50 text-primary-700 hover:bg-primary-100 hover:border-primary-300 transition-colors"
                          title={t('submissionsTable.coupleAnalysisTitle')}
                        >
                          {t('submissionsTable.coupleButton')}
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          onDelete(row)
                        }}
                        className="inline-flex items-center px-2.5 py-1 text-xs font-medium rounded-md border border-gray-200 text-gray-500 hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                        title={t('submissionsTable.deleteButton')}
                      >
                        {t('submissionsTable.deleteButton')}
                      </button>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}
